import React, { useEffect, useState } from 'react';
import { X, Building2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { clinicApi } from '../services/clinicApi';
import DuplicateWarningModal from './wizard/DuplicateWarningModal';
import ConfirmationModal from '@shared/components/common/ConfirmationModal';
import type { DuplicateCheckResult } from '../types/wizard';
import './SimpleAddClinicModal.css';

interface SimpleAddClinicModalProps {
  onClose: () => void;
  onComplete: (clinicId: number) => void;
}

interface FormState {
  name: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  fax: string;
}

const EMPTY_FORM: FormState = {
  name: '',
  address1: '',
  address2: '',
  city: '',
  state: '',
  zip: '',
  phone: '',
  fax: '',
};

type Errors = Partial<Record<keyof FormState, string>>;

function validate(form: FormState): Errors {
  const errors: Errors = {};
  if (!form.name.trim()) errors.name = 'Clinic name is required';
  if (!form.address1.trim()) errors.address1 = 'Address is required';
  if (!form.city.trim()) errors.city = 'City is required';
  if (!form.state.trim()) {
    errors.state = 'State is required';
  } else if (form.state.trim().length !== 2) {
    errors.state = 'Use the 2-letter state code';
  }
  if (!form.zip.trim()) {
    errors.zip = 'ZIP code is required';
  } else if (!/^\d{5}(-\d{4})?$/.test(form.zip.trim())) {
    errors.zip = 'ZIP must be 5 digits or ZIP+4';
  }
  return errors;
}

// Mounted only while open (parent conditionally renders it), so state starts
// fresh on every open and no reset effect is needed.
const SimpleAddClinicModal: React.FC<Readonly<SimpleAddClinicModalProps>> = ({
  onClose,
  onComplete,
}) => {
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<Errors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [duplicateResult, setDuplicateResult] = useState<DuplicateCheckResult | null>(null);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  // Cancel, X and Escape: never mid-submit (the request would resolve after
  // unmount), and ask before throwing away entries. A click outside the dialog
  // does not close it at all (SLV2-4049).
  const requestClose = () => {
    if (isSubmitting) return;
    if (Object.values(form).some((v) => v.trim())) setConfirmDiscard(true);
    else onClose();
  };

  useEffect(() => {
    // The duplicate and discard modals handle Escape themselves.
    if (showDuplicateModal || confirmDiscard) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') requestClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  });

  // Lock body scroll while open. DuplicateWarningModal and ConfirmationModal
  // manage their own lock and restore scrolling on close, so re-assert ours
  // whenever neither is showing.
  useEffect(() => {
    if (!showDuplicateModal && !confirmDiscard) document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [showDuplicateModal, confirmDiscard]);

  const update = (field: keyof FormState, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
    // Editing the matched fields invalidates a prior duplicate acknowledgement.
    setDuplicateResult(null);
  };

  // Performs the actual create (after any duplicate acknowledgement).
  const createClinic = async (acknowledgedDuplicateWarning: boolean) => {
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const result = await clinicApi.quickAddClinic({
        name: form.name.trim(),
        address1: form.address1.trim(),
        address2: form.address2.trim() || undefined,
        city: form.city.trim(),
        state: form.state.trim().toUpperCase(),
        zip: form.zip.trim(),
        phone: form.phone.trim() || undefined,
        fax: form.fax.trim() || undefined,
        acknowledgedDuplicateWarning,
      });
      onComplete(result.id);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string; title?: string } }; message?: string };
      setSubmitError(
        err.response?.data?.message ||
          err.response?.data?.title ||
          err.message ||
          'Failed to add clinic. Please try again.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors = validate(form);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    setIsSubmitting(true);
    setSubmitError(null);

    // Check for duplicates before creating (same contract the wizard uses).
    try {
      const result = await clinicApi.checkDuplicate(
        form.name.trim(),
        form.city.trim(),
        form.state.trim().toUpperCase(),
        form.zip.trim(),
      );
      if (result.hasPotentialDuplicates) {
        setDuplicateResult(result);
        setShowDuplicateModal(true);
        setIsSubmitting(false);
        return;
      }
    } catch {
      // Non-fatal: if the duplicate check fails, proceed with creation.
    }

    await createClinic(false);
  };

  const handleDuplicateContinue = async () => {
    setShowDuplicateModal(false);
    await createClinic(true);
  };

  const handleDuplicateEditExisting = (clinicId: number) => {
    setShowDuplicateModal(false);
    onClose();
    window.location.href = `/clinics/${clinicId}`;
  };

  return (
    <>
      <div className="simple-add-overlay">
        <div className="simple-add-modal" role="dialog" aria-modal="true" aria-labelledby="simple-add-title">
          <div className="simple-add-header">
            <div className="simple-add-title-row">
              <Building2 size={20} />
              <h2 id="simple-add-title">Add a Clinic</h2>
            </div>
            <button type="button" className="simple-add-close" onClick={requestClose} aria-label="Close" disabled={isSubmitting}>
              <X size={20} />
            </button>
          </div>

          <p className="simple-add-intro">
            <CheckCircle2 size={14} />
            <span>
              The clinic is usable right away. Our Clinic Network team will get an urgent task to
              finish setting it up.
            </span>
          </p>

          <form className="simple-add-body" onSubmit={handleSubmit}>
            <div className="simple-add-field">
              <label htmlFor="sa-name">Clinic Name *</label>
              <input
                id="sa-name"
                type="text"
                value={form.name}
                onChange={(e) => update('name', e.target.value)}
                className={errors.name ? 'has-error' : ''}
                autoFocus
              />
              {errors.name && <span className="simple-add-error-text">{errors.name}</span>}
            </div>

            <div className="simple-add-field">
              <label htmlFor="sa-address1">Address *</label>
              <input
                id="sa-address1"
                type="text"
                placeholder="Street address"
                value={form.address1}
                onChange={(e) => update('address1', e.target.value)}
                className={errors.address1 ? 'has-error' : ''}
              />
              {errors.address1 && <span className="simple-add-error-text">{errors.address1}</span>}
            </div>

            <div className="simple-add-field">
              <label htmlFor="sa-address2">Suite / Unit</label>
              <input
                id="sa-address2"
                type="text"
                placeholder="Suite, unit, building (optional)"
                value={form.address2}
                onChange={(e) => update('address2', e.target.value)}
              />
            </div>

            <div className="simple-add-row">
              <div className="simple-add-field simple-add-field-grow">
                <label htmlFor="sa-city">City *</label>
                <input
                  id="sa-city"
                  type="text"
                  value={form.city}
                  onChange={(e) => update('city', e.target.value)}
                  className={errors.city ? 'has-error' : ''}
                />
                {errors.city && <span className="simple-add-error-text">{errors.city}</span>}
              </div>
              <div className="simple-add-field simple-add-field-state">
                <label htmlFor="sa-state">State *</label>
                <input
                  id="sa-state"
                  type="text"
                  maxLength={2}
                  placeholder="ST"
                  value={form.state}
                  onChange={(e) => update('state', e.target.value.toUpperCase())}
                  className={errors.state ? 'has-error' : ''}
                />
                {errors.state && <span className="simple-add-error-text">{errors.state}</span>}
              </div>
              <div className="simple-add-field simple-add-field-zip">
                <label htmlFor="sa-zip">ZIP *</label>
                <input
                  id="sa-zip"
                  type="text"
                  value={form.zip}
                  onChange={(e) => update('zip', e.target.value)}
                  className={errors.zip ? 'has-error' : ''}
                />
                {errors.zip && <span className="simple-add-error-text">{errors.zip}</span>}
              </div>
            </div>

            <div className="simple-add-row">
              <div className="simple-add-field simple-add-field-grow">
                <label htmlFor="sa-phone">Phone</label>
                <input
                  id="sa-phone"
                  type="tel"
                  value={form.phone}
                  onChange={(e) => update('phone', e.target.value)}
                />
              </div>
              <div className="simple-add-field simple-add-field-grow">
                <label htmlFor="sa-fax">Fax</label>
                <input
                  id="sa-fax"
                  type="tel"
                  value={form.fax}
                  onChange={(e) => update('fax', e.target.value)}
                />
              </div>
            </div>

            {submitError && (
              <div className="simple-add-submit-error">
                <AlertCircle size={16} />
                <span>{submitError}</span>
              </div>
            )}

            <div className="simple-add-footer">
              <button type="button" className="simple-add-btn-secondary" onClick={requestClose} disabled={isSubmitting}>
                Cancel
              </button>
              <button type="submit" className="simple-add-btn-primary" disabled={isSubmitting}>
                {isSubmitting ? 'Adding...' : 'Add Clinic'}
              </button>
            </div>
          </form>
        </div>
      </div>

      <DuplicateWarningModal
        isOpen={showDuplicateModal}
        matches={duplicateResult?.matches ?? []}
        onEditExisting={handleDuplicateEditExisting}
        onContinue={handleDuplicateContinue}
        onCancel={() => setShowDuplicateModal(false)}
      />

      <ConfirmationModal
        isOpen={confirmDiscard}
        title="Discard this clinic?"
        message="Everything entered in this form will be lost."
        confirmText="Discard"
        cancelText="Keep Editing"
        danger
        onConfirm={onClose}
        onCancel={() => setConfirmDiscard(false)}
      />
    </>
  );
};

export default SimpleAddClinicModal;
